import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

const MODULE_LABELS = { t2m: '🌱 T2M', pb101: '🎓 PB101', lead: '📋 Lead Engine' };
const CHANNEL_LABELS = { text: '💬 Text', call: '📞 Call', email: '📧 Email', face_to_face: '🤝 Face to Face' };

const ALL_CLUBS = [
  { id: 'WA',  name: 'West Allis' },
  { id: 'MF',  name: 'Menomonee Falls' },
  { id: 'GB',  name: 'Green Bay' },
  { id: 'DEN', name: 'Denton' },
  { id: 'LEW', name: 'Lewisville' },
  { id: 'MCK', name: 'McKinney' },
];

const DEFAULT_PLAN_TYPES = [
  'Unlimited Nationwide', 'Unlimited Club Only', 'Social Membership',
  'Junior Membership', 'Punch Card', 'Drop-In', 'Corporate', 'Other',
];

// ─── Script Edit Modal ────────────────────────────────────────────────────────
function ScriptEditModal({ script, onClose, onSaved }) {
  const { put } = useApi();
  const [body, setBody] = useState(script.globalDefault || script.body || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await put(`/scripts/${script.id}`, { globalDefault: body, body });
      onSaved();
    } catch { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header"><h2>Edit Script</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div style={{ marginBottom: 12 }}>
            <span className="field-label">{script.scriptKey}</span>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              {MODULE_LABELS[script.module] || script.module} · {CHANNEL_LABELS[script.channel] || script.channel} · {script.trigger}
            </div>
          </div>
          <div className="form-field">
            <label className="field-label">Script Body</label>
            <textarea className="field-input" value={body} onChange={e => setBody(e.target.value)}
              rows={8} style={{ resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: 13 }} />
          </div>
          <div className="modal-hint"><span>💡</span><span>Placeholders: {'{firstName}'} {'{repName}'} {'{clubName}'} {'{currentPromo}'}</span></div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Script'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── New Script Modal ─────────────────────────────────────────────────────────
function NewScriptModal({ onClose, onCreated }) {
  const { post } = useApi();
  const { currentClub } = useClub();
  const [form, setForm] = useState({
    scriptKey: '', name: '', module: 't2m', channel: 'text',
    timing: '', scriptBody: '', whyContext: '', scope: 'global',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!form.scriptKey.trim()) { setError('Script key is required.'); return; }
    if (!form.name.trim())      { setError('Name is required.'); return; }
    if (!form.scriptBody.trim()){ setError('Script body is required.'); return; }
    setSaving(true); setError(null);
    try {
      await post('/scripts', { ...form, locationId: form.scope === 'club' ? currentClub.id : null });
      onCreated();
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header"><h2>New Script</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}
          <div className="form-row">
            <div className="form-field">
              <label className="field-label">Script Key <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="field-input" value={form.scriptKey} onChange={e => set('scriptKey', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                placeholder="t2m_day1_text" style={{ fontFamily: 'var(--font-mono)' }} autoFocus />
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>Lowercase, underscores only</span>
            </div>
            <div className="form-field">
              <label className="field-label">Display Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input className="field-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Day 1 Text" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label className="field-label">Module</label>
              <select className="field-input" value={form.module} onChange={e => set('module', e.target.value)}>
                <option value="t2m">🌱 T2M</option>
                <option value="pb101">🎓 PB101</option>
                <option value="lead_engine">📋 Lead Engine</option>
              </select>
            </div>
            <div className="form-field">
              <label className="field-label">Channel</label>
              <select className="field-input" value={form.channel} onChange={e => set('channel', e.target.value)}>
                <option value="text">💬 Text</option>
                <option value="call">📞 Call</option>
                <option value="email">📧 Email</option>
                <option value="face_to_face">🤝 Face to Face</option>
              </select>
            </div>
            <div className="form-field">
              <label className="field-label">Scope</label>
              <select className="field-input" value={form.scope} onChange={e => set('scope', e.target.value)}>
                <option value="global">Global (all clubs)</option>
                <option value="club">Club Override ({currentClub.name})</option>
              </select>
            </div>
          </div>
          <div className="form-field">
            <label className="field-label">Timing / Trigger</label>
            <input className="field-input" value={form.timing} onChange={e => set('timing', e.target.value)} placeholder="Day 1, after member leaves" />
          </div>
          <div className="form-field">
            <label className="field-label">Script Body <span style={{ color: 'var(--red)' }}>*</span></label>
            <textarea className="field-input" value={form.scriptBody} onChange={e => set('scriptBody', e.target.value)}
              rows={8} placeholder={"Hey {firstName}, this is {repName} from The Picklr {clubName}. ..."}
              style={{ resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: 13 }} />
          </div>
          <div className="form-field">
            <label className="field-label">Why This Script <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(1 sentence shown to staff)</span></label>
            <input className="field-input" value={form.whyContext} onChange={e => set('whyContext', e.target.value)}
              placeholder="First contact after member signs up for trial — sets the tone." />
          </div>
          <div className="modal-hint"><span>💡</span><span>Placeholders: {'{firstName}'} {'{repName}'} {'{clubName}'} {'{currentPromo}'}</span></div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Creating…' : 'Create Script'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Suggestion Review Modal ──────────────────────────────────────────────────
function SuggestionReviewModal({ suggestion, onClose, onSaved }) {
  const { put } = useApi();
  const [editedText, setEditedText] = useState(suggestion.suggestedText || '');
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(null);

  const handle = async (act) => {
    setSaving(true); setAction(act);
    try {
      await put(`/scripts/suggestions/${suggestion.id}`, { action: act, editedText: act === 'approve' ? editedText : undefined });
      onSaved();
    } catch { setSaving(false); setAction(null); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header"><h2>Review Suggestion</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            Script: <strong style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{suggestion.scriptKey}</strong>
            &nbsp;· Submitted by {suggestion.submittedBy} on {new Date(suggestion.submittedAt).toLocaleDateString()}
          </div>
          <div className="form-field">
            <label className="field-label">Suggested Text <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(edit before accepting)</span></label>
            <textarea className="field-input" value={editedText} onChange={e => setEditedText(e.target.value)}
              rows={8} style={{ resize: 'vertical', fontFamily: 'var(--font-mono)', fontSize: 13 }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-secondary" onClick={() => handle('decline')} disabled={saving} style={{ color: 'var(--red)', borderColor: 'var(--red)' }}>
            {saving && action === 'decline' ? 'Declining…' : 'Decline'}
          </button>
          <button className="btn-primary" onClick={() => handle('approve')} disabled={saving}>
            {saving && action === 'approve' ? 'Approving…' : 'Approve & Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Promo Modal (with Clubs + Eligible Plans) ────────────────────────────────
function PromoModal({ onClose, onCreated }) {
  const { post } = useApi();
  const [form, setForm] = useState({
    name: '', description: '', type: 'discount_pct', value: '',
    startDate: new Date().toISOString().split('T')[0], endDate: '', scriptPlaceholder: '',
  });
  const [selectedClubs, setSelectedClubs] = useState(['ALL']);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const toggleClub = (id) => {
    if (id === 'ALL') {
      setSelectedClubs(['ALL']);
    } else {
      setSelectedClubs(prev => {
        const without = prev.filter(c => c !== 'ALL');
        return without.includes(id) ? without.filter(c => c !== id) : [...without, id];
      });
    }
  };

  const togglePlan = (plan) => {
    setSelectedPlans(prev => prev.includes(plan) ? prev.filter(p => p !== plan) : [...prev, plan]);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (selectedClubs.length === 0) { setError('Select at least one club.'); return; }
    setSaving(true); setError(null);
    try {
      await post('/promos', {
        ...form,
        clubs: selectedClubs,
        eligiblePlans: selectedPlans.length > 0 ? selectedPlans : null,
      });
      onCreated();
    } catch (err) { setError(err.message); setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 680 }}>
        <div className="modal-header"><h2>New Promo</h2><button className="modal-close" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}

          <div className="form-field">
            <label className="field-label">Promo Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="field-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Spring Kickoff" autoFocus />
          </div>
          <div className="form-field">
            <label className="field-label">Description</label>
            <input className="field-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="20% off first month" />
          </div>
          <div className="form-row">
            <div className="form-field">
              <label className="field-label">Type</label>
              <select className="field-input" value={form.type} onChange={e => set('type', e.target.value)}>
                <option value="discount_pct">% Discount</option>
                <option value="discount_dollar">$ Discount</option>
                <option value="club_credit">Club Credit</option>
                <option value="retail_bundle">Retail Bundle</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-field">
              <label className="field-label">Value</label>
              <input className="field-input" type="number" value={form.value} onChange={e => set('value', e.target.value)} placeholder="20" />
            </div>
          </div>
          <div className="form-row">
            <div className="form-field">
              <label className="field-label">Start Date</label>
              <input className="field-input" type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
            </div>
            <div className="form-field">
              <label className="field-label">End Date</label>
              <input className="field-input" type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
            </div>
          </div>

          {/* Club assignment */}
          <div className="form-field">
            <label className="field-label">Clubs <span style={{ color: 'var(--red)' }}>*</span></label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              <button
                onClick={() => setSelectedClubs(['ALL'])}
                style={{
                  background: selectedClubs.includes('ALL') ? 'var(--yellow)' : 'var(--surface-2)',
                  color: selectedClubs.includes('ALL') ? '#000' : 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 6, padding: '4px 12px',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                }}
              >
                All Clubs
              </button>
              {ALL_CLUBS.map(c => (
                <button
                  key={c.id}
                  onClick={() => toggleClub(c.id)}
                  style={{
                    background: selectedClubs.includes(c.id) ? 'var(--yellow)' : 'var(--surface-2)',
                    color: selectedClubs.includes(c.id) ? '#000' : 'var(--text)',
                    border: '1px solid var(--border)', borderRadius: 6, padding: '4px 12px',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {/* Eligible plans */}
          <div className="form-field">
            <label className="field-label">Eligible Plans <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(leave blank = applies to any plan)</span></label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {DEFAULT_PLAN_TYPES.map(plan => (
                <button
                  key={plan}
                  onClick={() => togglePlan(plan)}
                  style={{
                    background: selectedPlans.includes(plan) ? 'rgba(234,179,8,0.2)' : 'var(--surface-2)',
                    color: selectedPlans.includes(plan) ? 'var(--yellow)' : 'var(--text-muted)',
                    border: `1px solid ${selectedPlans.includes(plan) ? 'var(--yellow)' : 'var(--border)'}`,
                    borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                  }}
                >
                  {plan}
                </button>
              ))}
            </div>
          </div>

          <div className="form-field">
            <label className="field-label">Script Placeholder Text</label>
            <input className="field-input" value={form.scriptPlaceholder} onChange={e => set('scriptPlaceholder', e.target.value)} placeholder="20% off your first month with code SPRING20" />
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'block' }}>Replaces {'{currentPromo}'} in scripts</span>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Creating…' : 'Create Promo'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AdminScripts() {
  const { get } = useApi();
  const [subtab, setSubtab] = useState('Scripts');
  const [scripts, setScripts] = useState([]);
  const [promos, setPromos] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editScript, setEditScript] = useState(null);
  const [showNewScript, setShowNewScript] = useState(false);
  const [reviewSuggestion, setReviewSuggestion] = useState(null);
  const [showPromo, setShowPromo] = useState(false);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, sg] = await Promise.all([get('/scripts'), get('/promos/active'), get('/scripts/suggestions')]);
      setScripts(s.scripts || []);
      setPromos(p.promos || []);
      setSuggestions(sg.suggestions || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAll(); }, [loadAll]);

  const pendingCount = suggestions.filter(s => s.status === 'pending').length;
  const flaggedCount = scripts.filter(s => (s.deviationCount || 0) >= 3).length;

  const filteredScripts = scripts.filter(s => {
    if (moduleFilter !== 'all' && s.module !== moduleFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (s.scriptKey || '').toLowerCase().includes(q) || (s.name || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div>
      {/* KPI bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Total Scripts',   value: scripts.length },
          { label: 'Pending Suggest', value: pendingCount,  color: pendingCount > 0 ? 'var(--yellow)' : undefined },
          { label: 'Flagged',         value: flaggedCount,  color: flaggedCount > 0 ? 'var(--red)' : undefined },
          { label: 'Active Promos',   value: promos.length, color: 'var(--green)' },
        ].map(k => (
          <div key={k.label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 7, padding: '6px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>{k.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: k.color || 'var(--text)', fontFamily: 'DM Mono, monospace' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Subtab bar */}
      <div className="subtab-bar" style={{ marginBottom: 16 }}>
        {[
          { key: 'Scripts',     label: 'Scripts' },
          { key: 'Promos',      label: 'Promo Manager' },
          { key: 'Suggestions', label: `Suggestions${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
          { key: 'Flagged',     label: `Flagged${flaggedCount > 0 ? ` (${flaggedCount})` : ''}` },
          { key: 'CustomLog',   label: 'Custom Log' },
        ].map(t => (
          <button key={t.key} className={`subtab ${subtab === t.key ? 'active' : ''}`} onClick={() => setSubtab(t.key)}>{t.label}</button>
        ))}
      </div>

      {loading ? <div className="empty-state"><div className="spinner" /></div> : (
        <>
          {subtab === 'Scripts' && (
            <div>
              {editScript    && <ScriptEditModal script={editScript} onClose={() => setEditScript(null)} onSaved={() => { setEditScript(null); loadAll(); }} />}
              {showNewScript && <NewScriptModal onClose={() => setShowNewScript(false)} onCreated={() => { setShowNewScript(false); loadAll(); }} />}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input className="field-input" type="text" placeholder="Search scripts..." value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} />
                  <select className="field-input" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} style={{ width: 160 }}>
                    <option value="all">All Modules</option>
                    <option value="t2m">🌱 T2M</option>
                    <option value="pb101">🎓 PB101</option>
                    <option value="lead_engine">📋 Lead Engine</option>
                  </select>
                </div>
                <button className="btn-primary" onClick={() => setShowNewScript(true)}>+ New Script</button>
              </div>
              {filteredScripts.length === 0 ? (
                <div className="empty-state"><div className="empty-state-text">No scripts found.</div></div>
              ) : (
                filteredScripts.map(s => (
                  <div key={s.id} style={{ background: 'var(--surface-2)', border: `1px solid ${(s.deviationCount || 0) >= 3 ? 'var(--red)' : 'var(--border)'}`, borderRadius: 8, marginBottom: 8, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{s.name || s.scriptKey}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                          <span style={{ background: 'var(--surface-3)', color: 'var(--text-muted)', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>{MODULE_LABELS[s.module] || s.module}</span>
                          <span style={{ background: 'var(--surface-3)', color: 'var(--text-muted)', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>{CHANNEL_LABELS[s.channel] || s.channel}</span>
                          <span style={{ background: s.scope === 'club' ? 'rgba(234,179,8,0.2)' : 'var(--surface-3)', color: s.scope === 'club' ? 'var(--yellow)' : 'var(--text-muted)', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>
                            {s.scope === 'club' ? '★ CLUB OVERRIDE' : 'GLOBAL'}
                          </span>
                          {(s.deviationCount || 0) >= 3 && (
                            <span style={{ background: 'rgba(220,38,38,0.2)', color: 'var(--red)', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>⚑ {s.deviationCount} DEVIATIONS</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 500 }}>
                          {s.clubOverride || s.globalDefault || s.body || '—'}
                        </div>
                      </div>
                      <button className="btn-secondary btn-xs" onClick={() => setEditScript(s)} style={{ marginLeft: 12 }}>Edit</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {subtab === 'Promos' && (
            <div>
              {showPromo && <PromoModal onClose={() => setShowPromo(false)} onCreated={() => { setShowPromo(false); loadAll(); }} />}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button className="btn-primary" onClick={() => setShowPromo(true)}>+ New Promo</button>
              </div>
              {promos.length === 0 ? (
                <div className="empty-state"><div className="empty-state-text">No active promos.</div></div>
              ) : (
                promos.map(p => (
                  <div key={p.id} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{p.description}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ background: 'rgba(34,197,94,0.15)', color: 'var(--green)', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>ACTIVE</span>
                      <span style={{ background: 'var(--surface-3)', color: 'var(--text-muted)', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>
                        {p.type?.replace('_', ' ').toUpperCase()} · {p.value}{p.type === 'discount_pct' ? '%' : ''}
                      </span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.startDate} → {p.endDate || '∞'}</span>
                      {p.clubs && !p.clubs.includes('ALL') && (
                        <span style={{ background: 'rgba(59,130,246,0.15)', color: '#60a5fa', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>
                          {Array.isArray(p.clubs) ? p.clubs.join(', ') : p.clubs}
                        </span>
                      )}
                    </div>
                    {p.eligiblePlans && p.eligiblePlans.length > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                        Plans: {Array.isArray(p.eligiblePlans) ? p.eligiblePlans.join(', ') : p.eligiblePlans}
                      </div>
                    )}
                    {p.scriptPlaceholder && (
                      <div style={{ fontSize: 11, color: 'var(--yellow)', fontStyle: 'italic' }}>Script auto-fill: "{p.scriptPlaceholder}"</div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {subtab === 'Suggestions' && (
            <div>
              {reviewSuggestion && <SuggestionReviewModal suggestion={reviewSuggestion} onClose={() => setReviewSuggestion(null)} onSaved={() => { setReviewSuggestion(null); loadAll(); }} />}
              {suggestions.length === 0 ? (
                <div className="empty-state"><div className="empty-state-icon">✅</div><div className="empty-state-text">No pending suggestions.</div></div>
              ) : (
                suggestions.map(s => (
                  <div key={s.id} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--yellow)', marginBottom: 4 }}>{s.scriptKey}</div>
                        <div style={{ fontSize: 13, marginBottom: 8 }}>{s.suggestedText}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Submitted by {s.submittedBy} · {new Date(s.submittedAt).toLocaleDateString()}</div>
                      </div>
                      <button style={{ background: 'var(--yellow)', color: '#000', border: 'none', borderRadius: 5, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginLeft: 12, whiteSpace: 'nowrap' }}
                        onClick={() => setReviewSuggestion(s)}>Review</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {subtab === 'Flagged' && (
            <div>
              {editScript && <ScriptEditModal script={editScript} onClose={() => setEditScript(null)} onSaved={() => { setEditScript(null); loadAll(); }} />}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, fontStyle: 'italic' }}>
                Scripts where staff deviated 3+ times. Review and update if the deviation reflects a better approach.
              </div>
              {scripts.filter(s => (s.deviationCount || 0) >= 3).length === 0 ? (
                <div className="empty-state"><div className="empty-state-icon">✅</div><div className="empty-state-text">No flagged scripts.</div></div>
              ) : (
                scripts.filter(s => (s.deviationCount || 0) >= 3).map(s => (
                  <div key={s.id} style={{ background: 'var(--surface-2)', border: '1px solid var(--red)', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{s.name || s.scriptKey}</div>
                        <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 6 }}>⚑ {s.deviationCount} deviations recorded</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.clubOverride || s.globalDefault || s.body || '—'}</div>
                      </div>
                      <button className="btn-secondary btn-xs" onClick={() => setEditScript(s)} style={{ marginLeft: 12 }}>Edit</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {subtab === 'CustomLog' && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, fontStyle: 'italic' }}>
                Staff-logged custom script entries — face-to-face conversations where staff paraphrased rather than read verbatim.
              </div>
              <div className="empty-state"><div className="empty-state-icon">📝</div><div className="empty-state-text">No custom script entries logged yet.</div></div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
